import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Fields } from "../../types/SchemaFactory";
import CustomDropdown from "../../custom-components/form-inputs/CustomDropdown";
import { enumToSelectProps, enumValueToSelectProps } from "../../tools/EnumTools";
import { PaginationEnum } from "../../domain/enums/common/PaginationEnum";
import FilterResetIconSVG from "./svg/FilterResetIconSVG";
import { GenericFiltersForm } from "./GenericFiltersForm";
import { schemaFactory } from "../../validation-schemas/SchemaFactory";

interface GenericEntityPageHeaderProps<F> {
    filtersFieldsMeta: Fields<F>;
    filters: F;
    filtersSchema: ReturnType<typeof schemaFactory>;
    setFilters: (f: F) => void;
    setLimit: (n: number) => void;
    openEditor: (id?: string) => void;
}

const getSectionTitleKey = (pathname: string): string => {
    if (pathname.includes("/news")) return "console.sidebar.newsPage";
    if (pathname.includes("/management")) return "console.sidebar.managementPage";
    if (pathname.includes("/partners")) return "console.sidebar.partnersPage";
    if (pathname.includes("/events")) return "console.sidebar.eventsPage";
    if (pathname.includes("/development-history")) return "console.sidebar.developmentHistoryPage";
    if (pathname.includes("/courses")) return "console.sidebar.coursesPage";
    return "";
};

export default function GenericEntityPageHeader<F>({
    filtersFieldsMeta,
    filters,
    filtersSchema,
    setFilters,
    setLimit,
    openEditor,
}: GenericEntityPageHeaderProps<F>) {
    const { t } = useTranslation();
    const location = useLocation();
    const sectionTitleKey = getSectionTitleKey(location.pathname);

    return (
        <div className="bg-light-bg-100 dark:bg-dark-bg-100 w-[95%] mb-[10px] mt-[10px] mx-auto py-2 px-3 flex items-center justify-between shadow-md rounded-xl">
            {sectionTitleKey && (
                <h2 className="text-[20px] font-bold text-light-text-100 dark:text-dark-text-100 ml-4">
                    {t(sectionTitleKey)}
                </h2>
            )}
            <div className="flex items-center gap-4">
                <button
                    className="bg-green-500 hover:bg-green-600 fill-white flex items-center text-white focus:ring-2 focus:outline-none font-medium rounded-lg text-sm px-4 py-2.5 text-center h-10"
                    onClick={() => openEditor()}
                >
                    {t("console.common.create")}
                </button>
                <div className="flex items-center gap-2">
                    <h3 className="text-[16px] font-semibold text-light-text-100 dark:text-dark-text-100">
                        {t("pagination.show")}
                    </h3>
                    <CustomDropdown
                        defaultValue={enumValueToSelectProps(PaginationEnum.SHOW_20, "pagination")}
                        data={enumToSelectProps(PaginationEnum, "pagination")}
                        dropDownCallback={(val) => setLimit(val as number)}
                        dropdownClassName="w-[80px]"
                    />
                </div>
                <GenericFiltersForm
                    fieldsMeta={filtersFieldsMeta}
                    filters={filters}
                    schema={filtersSchema}
                    onSubmit={(v) => setFilters(v as F)}
                />
                <button
                    className="flex items-center text-white bg-light-bg-500 dark:bg-dark-bg-500 hover:bg-light-bg-600 dark:hover:bg-dark-bg-600 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-4 py-2.5 text-center h-10"
                    onClick={() => setFilters({} as F)}
                    title={t("console.common.resetFilters")}
                >
                    <FilterResetIconSVG className="w-[20px] fill-white" />
                </button>
            </div>
        </div>
    );
}
